import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Archive, Ban, Calendar, CheckCircle2, CheckSquare, ChevronLeft, CloudUpload, Plus, Repeat, Send, ShieldCheck, Square, ArrowLeftRight, Trash2, XCircle } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, compressImage, pickImage } from "@/lib/api";
import { isManagerRole, useAuth } from "@/lib/auth";
import { useToast } from "@/lib/toast";
import { Button, Card, Input, Overline } from "@/components/app/Primitives";
import { PriorityBadge, StatusBadge } from "@/components/app/Badges";
import { StackPage, useBack } from "@/components/app/Layout";
import { CenterSpinner, Spinner } from "@/components/app/Spinner";

const ACTION_TOAST = { submit: "Submitted for approval", approve: "Approved", accept: "Task accepted", start: "In progress" };

function Pills({ items, value, onPick }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map(([id, name]) => (
        <button key={id} onClick={() => onPick(id)} className={`rounded-full border px-3 py-1.5 text-xs font-bold ${value === id ? "border-a-primary bg-a-primary text-white" : "border-a-border text-a-text"}`}>{name}</button>
      ))}
    </div>
  );
}

export default function TaskDetail() {
  const { id } = useParams();
  const toast = useToast();
  const navigate = useNavigate();
  const back = useBack("/tasks");
  const { user, activeWorkspace } = useAuth();
  const [task, setTask] = useState(null);
  const [members, setMembers] = useState([]);
  const [comments, setComments] = useState([]);
  const [newItem, setNewItem] = useState("");
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [proofBusy, setProofBusy] = useState(false);
  const [proofData, setProofData] = useState({});
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectRemarks, setRejectRemarks] = useState("");
  const [terminateOpen, setTerminateOpen] = useState(false);
  const [terminateReason, setTerminateReason] = useState("");
  const [reassignOpen, setReassignOpen] = useState(false);
  const [fromId, setFromId] = useState("");
  const [toId, setToId] = useState("");
  const [reassignReason, setReassignReason] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const t = await api(`/tasks/${id}`);
      setTask(t);
      setComments(await api(`/tasks/${id}/comments`));
      try { if (t.workspace_id) setMembers(await api(`/workspaces/${t.workspace_id}/members`)); } catch { /* non-fatal */ }
    } catch (e) { toast.show(e instanceof Error ? e.message : "Failed to load task", "error"); }
    finally { setLoading(false); }
  }, [id, toast]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!task?.proofs) return;
    task.proofs.forEach(async (p) => {
      if (!p.mime_type?.startsWith("image/") || proofData[p.id]) return;
      try { const r = await api(`/tasks/${task.id}/proofs/${p.id}/data`); setProofData((prev) => ({ ...prev, [p.id]: `data:${r.mime_type};base64,${r.data_base64}` })); } catch { /* noop */ }
    });
  }, [task?.proofs]); // eslint-disable-line

  const run = async (fn, okMsg, kind = "success", failMsg = "Action failed") => {
    setBusy(true);
    try { const r = await fn(); if (r) setTask(r); if (okMsg) toast.show(okMsg, kind); return true; }
    catch (e) { toast.show(e instanceof Error ? e.message : failMsg, "error"); return false; }
    finally { setBusy(false); }
  };
  const doAction = (a) => run(() => api(`/tasks/${task.id}/${a}`, { method: "POST" }), ACTION_TOAST[a]);
  const doReject = async () => {
    if (!rejectRemarks.trim()) return toast.show("Please enter rejection remarks", "error");
    if (await run(() => api(`/tasks/${task.id}/reject`, { method: "POST", body: { remarks: rejectRemarks.trim() } }), "Sent back to employee with remarks", "info", "Reject failed")) { setRejectOpen(false); setRejectRemarks(""); }
  };
  const doTerminate = async () => {
    if (!terminateReason.trim()) return toast.show("Please enter a reason for terminating this task.", "error");
    if (await run(() => api(`/tasks/${task.id}/terminate`, { method: "POST", body: { remarks: terminateReason.trim() } }), "Task terminated. It will appear in reports and the Archived tab.", "info", "Terminate failed")) { setTerminateOpen(false); setTerminateReason(""); }
  };
  const doReassign = async () => {
    if (!fromId || !toId) return toast.show("Pick both the current and new assignee.", "error");
    if (await run(() => api(`/tasks/${task.id}/reassign`, { method: "POST", body: { from_assignee_id: fromId, new_assignee_id: toId, remarks: reassignReason.trim() } }), "Task reassigned. The new teammate has been notified.", "success", "Reassign failed")) {
      setReassignOpen(false); setFromId(""); setToId(""); setReassignReason("");
    }
  };
  const doArchive = async () => {
    try { await api(`/tasks/${task.id}/archive`, { method: "POST" }); toast.show("Task archived", "info"); back(); }
    catch (e) { toast.show(e instanceof Error ? e.message : "Archive failed", "error"); }
  };
  const toggleItem = async (item) => {
    try { setTask(await api(`/tasks/${task.id}/checklist/${item.id}`, { method: "PATCH", query: { done: !item.done } })); }
    catch (e) { toast.show(e instanceof Error ? e.message : "Failed", "error"); }
  };
  const addItem = async () => {
    if (!newItem.trim()) return;
    try { await api(`/tasks/${task.id}/checklist`, { method: "POST", body: { text: newItem.trim() } }); setNewItem(""); load(); }
    catch (e) { toast.show(e instanceof Error ? e.message : "Failed", "error"); }
  };
  const sendComment = async () => {
    if (!newComment.trim()) return;
    try { const c = await api(`/tasks/${task.id}/comments`, { method: "POST", body: { text: newComment.trim() } }); setComments((p) => [...p, c]); setNewComment(""); }
    catch (e) { toast.show(e instanceof Error ? e.message : "Failed", "error"); }
  };
  const deleteTask = async () => {
    try { await api(`/tasks/${task.id}`, { method: "DELETE" }); toast.show("Task deleted", "success"); navigate("/tasks", { replace: true }); }
    catch (e) { toast.show(e instanceof Error ? e.message : "Delete failed", "error"); }
  };
  const uploadProof = async () => {
    const file = await pickImage();
    if (!file) return;
    setProofBusy(true);
    try {
      const img = await compressImage(file, 1280, 0.6);
      await api(`/tasks/${task.id}/proofs`, { method: "POST", body: { data_base64: img.base64, mime_type: img.mimeType, note: "" } });
      toast.show("Proof uploaded", "success"); load();
    } catch (e) { toast.show(e instanceof Error ? e.message : "Upload failed", "error"); } finally { setProofBusy(false); }
  };
  const removeProof = async (pid) => {
    try { await api(`/tasks/${task.id}/proofs/${pid}`, { method: "DELETE" }); load(); }
    catch (e) { toast.show(e instanceof Error ? e.message : "Delete failed", "error"); }
  };

  if (loading || !task) {
    return <StackPage title="Task" onBack={back}>{loading ? <CenterSpinner /> : <Card><p className="text-a-muted">Task not found.</p></Card>}</StackPage>;
  }

  const canManage = isManagerRole(activeWorkspace?.role) || (user && task.creator_id === user.id);
  const states = task.assignee_states || {};
  const pendingAssignees = (task.assignee_ids || []).filter((a) => (states[a]?.status || "pending") === "pending");
  const hasPending = pendingAssignees.length > 0;
  const dl = task.deadline ? new Date(task.deadline) : null;
  const dlPassed = !!dl && !isNaN(dl.getTime()) && dl.getTime() < Date.now();
  const canTerminate = hasPending && dlPassed;
  const blocked = !hasPending ? "All assignees have already accepted this task — it can no longer be terminated." : !dl ? "Set a deadline first — a task can only be terminated after its deadline has passed." : !dlPassed ? `Termination is only available after the deadline (${dl.toLocaleString()}).` : "";
  const doneCount = task.checklist.filter((c) => c.done).length;
  const mainLabel = { pending: "Accept task", accepted: "Start working", in_progress: "Submit for approval", waiting_approval: "Waiting for manager approval", approved: "Approved" }[task.status] || "Task complete";
  const mainAction = { pending: "accept", accepted: "start", in_progress: "submit" }[task.status];
  const nameOf = (aid) => task.assignees?.find((x) => x.id === aid)?.full_name || aid;

  return (
    <StackPage title="Task" onBack={back} backIcon={ChevronLeft} testID="task-detail-page"
      right={<button onClick={() => setConfirmDelete(true)} className="text-a-danger hover:opacity-75" data-testid="task-delete-button"><Trash2 size={22} /></button>}>
      <Card>
        <div className="space-y-2.5">
          <div className="flex justify-between gap-2"><h2 className="flex-1 text-xl font-extrabold text-a-text" data-testid="task-title">{task.title}</h2><PriorityBadge priority={task.priority} /></div>
          {task.description ? <p className="whitespace-pre-wrap text-sm leading-5 text-a-muted">{task.description}</p> : null}
          <div className="flex flex-wrap items-center gap-2.5">
            <StatusBadge status={task.status} testID="task-status-badge" />
            {task.recurrence && task.recurrence !== "none" ? <span className="flex items-center gap-1 rounded-md bg-a-dim px-2 py-[3px] text-[11px] font-bold text-a-primary"><Repeat size={12} />{task.recurrence.charAt(0).toUpperCase() + task.recurrence.slice(1)}</span> : null}
            {dl ? <span className="flex items-center gap-1 text-xs text-a-muted"><Calendar size={13} />{dl.toLocaleDateString()}</span> : null}
          </div>
        </div>
      </Card>

      <Card>
        <Overline>Progress · {task.progress ?? 0}%</Overline>
        <div className="mt-2.5 h-2 overflow-hidden rounded-lg bg-a-border"><div className="h-2 rounded-lg bg-a-primary transition-[width] duration-500" style={{ width: `${task.progress ?? 0}%` }} /></div>
        <p className="mt-2.5 text-xs text-a-muted">Status changes are automatic — use the action button below to move to the next step.</p>
      </Card>

      <Card>
        <div className="mb-2 flex justify-between"><Overline>Checklist</Overline><span className="text-xs text-a-muted">{doneCount}/{task.checklist.length}</span></div>
        {task.checklist.length === 0 ? <p className="text-a-muted">No items yet.</p> : task.checklist.map((c) => (
          <button key={c.id} onClick={() => toggleItem(c)} data-testid={`checklist-item-${c.id}`} className="flex w-full items-center gap-2.5 py-2 text-left">
            {c.done ? <CheckSquare size={22} className="text-a-success" /> : <Square size={22} className="text-a-muted" />}
            <span className={`flex-1 text-a-text ${c.done ? "line-through opacity-60" : ""}`}>{c.text}</span>
          </button>
        ))}
        <div className="mt-2 flex gap-2">
          <Input testID="task-detail-checklist-input" placeholder="Add checklist item…" value={newItem} onChangeText={setNewItem} onSubmitEditing={addItem} />
          <button onClick={addItem} data-testid="task-detail-checklist-add" className="flex w-12 shrink-0 items-center justify-center rounded-xl bg-a-primary text-white hover:brightness-110"><Plus size={22} /></button>
        </div>
      </Card>

      <Card>
        <div className="mb-2 flex items-center justify-between"><Overline>Proof of work</Overline><span className="flex items-center gap-1 text-[11px] text-a-muted"><ShieldCheck size={12} />Auto-deleted on approval</span></div>
        {(task.proofs || []).length === 0 ? <p className="mb-3 text-[13px] text-a-muted">Attach a photo as evidence that this task was completed.</p> : (
          <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {task.proofs.map((p) => (
              <div key={p.id} className="overflow-hidden rounded-xl border border-a-border">
                {proofData[p.id] ? <img src={proofData[p.id]} alt="proof" className="h-48 w-full object-cover" /> : <div className="flex h-48 items-center justify-center bg-a-bg"><Spinner /></div>}
                <div className="flex items-center gap-2 p-2">
                  <p className="flex-1 truncate text-[11px] text-a-muted">{p.uploader_name || "Someone"} · {new Date(p.uploaded_at).toLocaleString()}</p>
                  <button onClick={() => removeProof(p.id)} className="text-a-danger"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
        <Button testID="task-upload-proof" label={proofBusy ? "Uploading…" : "Upload photo proof"} onPress={uploadProof} loading={proofBusy} variant="secondary" icon={<CloudUpload size={16} />} />
      </Card>

      <Card>
        <Overline className="mb-2">Comments</Overline>
        {comments.length === 0 ? <p className="text-a-muted">Be the first to comment.</p> : comments.map((c) => (
          <div key={c.id} className="border-b border-a-border py-2.5 last:border-0">
            <p className="text-[13px] font-bold text-a-text">{c.author_name}</p>
            <p className="mt-0.5 whitespace-pre-wrap text-a-text">{c.text}</p>
            <p className="mt-1 text-[11px] text-a-muted">{new Date(c.created_at).toLocaleString()}</p>
          </div>
        ))}
        <div className="mt-2 flex gap-2">
          <Input testID="task-comment-input" placeholder="Add a comment…" value={newComment} onChangeText={setNewComment} onSubmitEditing={sendComment} />
          <button onClick={sendComment} data-testid="task-comment-send" className="flex w-12 shrink-0 items-center justify-center rounded-xl bg-a-primary text-white hover:brightness-110"><Send size={18} /></button>
        </div>
      </Card>

      <Card>
        <Overline className="mb-2">Timeline</Overline>
        {[["created_at", "Created"], ["started_at", "Started"], ["completed_at", "Completed"]].map(([k, l]) => (
          <div key={k} className="flex justify-between py-1.5"><span className="text-a-muted">{l}</span><span className="text-a-text">{task[k] ? new Date(task[k]).toLocaleString() : "—"}</span></div>
        ))}
      </Card>

      <Button testID="task-main-action" label={mainLabel} onPress={() => mainAction && doAction(mainAction)} loading={busy} disabled={!mainAction} />

      {task.status === "waiting_approval" ? (
        <div className="flex gap-2.5">
          <div className="flex-1"><Button testID="task-approve" label="Approve" onPress={() => doAction("approve")} loading={busy} icon={<CheckCircle2 size={16} />} /></div>
          <div className="flex-1"><Button testID="task-reject" label="Reject" variant="danger" onPress={() => setRejectOpen(true)} icon={<XCircle size={16} />} /></div>
        </div>
      ) : null}

      {rejectOpen ? (
        <Card className="space-y-2.5 !border-a-danger">
          <p className="font-extrabold text-a-text">Reject with remarks</p>
          <p className="text-[13px] text-a-muted">Tell the employee what needs correction. The task will return to In Progress.</p>
          <Input testID="task-reject-remarks" placeholder="e.g. Please attach the signed vendor form" value={rejectRemarks} onChangeText={setRejectRemarks} multiline rows={3} />
          <div className="flex gap-2.5"><div className="flex-1"><Button label="Cancel" variant="ghost" onPress={() => { setRejectOpen(false); setRejectRemarks(""); }} /></div><div className="flex-1"><Button testID="task-reject-send" label="Send back" variant="danger" onPress={doReject} loading={busy} /></div></div>
        </Card>
      ) : null}

      {task.status === "in_progress" && task.rejection_remarks ? (
        <Card className="!border-a-warning"><p className="font-extrabold text-a-warning">Rejection remarks</p><p className="mt-1 text-a-text">{task.rejection_remarks}</p></Card>
      ) : null}

      {canManage && !["approved", "terminated"].includes(task.status) && !task.archived ? (
        <Card testID="task-manager-tools">
          <Overline>Manager tools</Overline>
          <p className="mt-1 text-xs text-a-muted">Reassign the task to another teammate if the current assignee hasn&apos;t accepted yet. You can only terminate a task once its deadline has passed and the assignee still hasn&apos;t accepted.</p>
          {hasPending ? (
            <div className="mt-3 flex gap-2.5">
              <div className="flex-1"><Button testID="task-terminate" label="Terminate" variant="danger" disabled={!canTerminate} onPress={() => (canTerminate ? setTerminateOpen((v) => !v) : toast.show(blocked, "info"))} icon={<Ban size={16} />} /></div>
              <div className="flex-1"><Button testID="task-reassign" label="Reassign" variant="ghost" onPress={() => setReassignOpen((v) => !v)} icon={<ArrowLeftRight size={16} />} /></div>
            </div>
          ) : null}
          {hasPending && !canTerminate ? <p className="mt-2 text-[11px] text-a-muted">{blocked}</p> : null}
        </Card>
      ) : null}

      {terminateOpen ? (
        <Card className="space-y-2.5 !border-a-danger">
          <p className="font-extrabold text-a-text">Terminate this task</p>
          <p className="text-[13px] text-a-muted">The task will be marked <b className="text-a-danger">Terminated</b> and appear in reports and the Archived tab. Assignees will be notified.</p>
          <Input testID="task-terminate-reason" placeholder="Reason (e.g. Project cancelled, no longer required)" value={terminateReason} onChangeText={setTerminateReason} multiline rows={3} />
          <div className="flex gap-2.5"><div className="flex-1"><Button label="Cancel" variant="ghost" onPress={() => { setTerminateOpen(false); setTerminateReason(""); }} /></div><div className="flex-1"><Button testID="task-terminate-confirm" label="Terminate task" variant="danger" onPress={doTerminate} loading={busy} /></div></div>
        </Card>
      ) : null}

      {reassignOpen ? (
        <Card className="space-y-2.5 !border-a-primary">
          <p className="font-extrabold text-a-text">Reassign task</p>
          <p className="text-[13px] text-a-muted">Only pending (not-yet-accepted) assignments can be reassigned. Pick who is being replaced and who should take over.</p>
          <p className="text-[11px] font-extrabold tracking-[1.2px] text-a-muted">FROM (currently pending)</p>
          <Pills items={pendingAssignees.map((a) => [a, nameOf(a)])} value={fromId} onPick={setFromId} />
          <p className="text-[11px] font-extrabold tracking-[1.2px] text-a-muted">TO (new assignee)</p>
          <Pills items={members.filter((m) => !task.assignee_ids.includes(m.user_id) && m.user_id !== user?.id).slice(0, 30).map((m) => [m.user_id, m.full_name])} value={toId} onPick={setToId} />
          <Input testID="task-reassign-reason" placeholder="Reason (optional — e.g. On leave, needs a specialist)" value={reassignReason} onChangeText={setReassignReason} />
          <div className="flex gap-2.5"><div className="flex-1"><Button label="Cancel" variant="ghost" onPress={() => { setReassignOpen(false); setFromId(""); setToId(""); setReassignReason(""); }} /></div><div className="flex-1"><Button testID="task-reassign-confirm" label="Reassign" onPress={doReassign} loading={busy} /></div></div>
        </Card>
      ) : null}

      {task.status === "terminated" ? (
        <Card className="!border-a-danger"><p className="font-extrabold text-a-danger">Task terminated</p>{task.termination_reason ? <p className="mt-1 text-a-text">{task.termination_reason}</p> : null}</Card>
      ) : null}
      {task.status === "approved" && !task.archived ? <Button testID="task-archive" label="Archive task" variant="ghost" onPress={doArchive} icon={<Archive size={16} />} /> : null}
      {task.status === "terminated" && !task.archived && canManage ? <Button testID="task-archive" label="Move to archive" variant="ghost" onPress={doArchive} icon={<Archive size={16} />} /> : null}

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="border-a-border bg-a-surface text-a-text">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this task?</AlertDialogTitle>
            <AlertDialogDescription className="text-a-muted">This permanently removes the task, its checklist, comments and proofs.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-a-border bg-transparent text-a-text">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteTask} className="bg-a-danger text-white hover:bg-a-danger/90" data-testid="task-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </StackPage>
  );
}
